import * as THREE from 'three';
import type { PhysicsWorld } from '../physics/PhysicsWorld';
import { Chunk, residentKeysAt } from './Chunk';
import { heightAt } from './Heightmap';

export {
  CHUNK_MAX,
  CHUNK_MIN,
  chunkCenter,
  chunkIndex,
  chunkKey,
  chunkOrigin,
  clampChunkIndex,
  inIslandChunk,
  parseChunkKey,
  RESIDENT_RADIUS,
  residentKeysAt,
  worldToChunk,
} from './Chunk';
export {
  CHUNK_SIZE,
  HEIGHT_CELLS,
  HEIGHT_VERTS,
  heightAt,
  inIsland,
  ISLAND_CHUNKS,
  ISLAND_HALF,
  ISLAND_MAX,
  ISLAND_MIN,
  ISLAND_SIZE,
  sampleChunkHeights,
  setHeightSampler,
} from './Heightmap';

/**
 * 64 m visual + collider ring around the player.
 * Never touches PhysicsWorld.groundBody (the hx=280 hz=320 safety crust).
 */
export class Streamer {
  private readonly root: THREE.Group;
  private readonly physics: PhysicsWorld;
  private readonly loaded = new Map<string, Chunk>();

  constructor(scene: THREE.Scene, physics: PhysicsWorld) {
    this.physics = physics;
    this.root = new THREE.Group();
    this.root.name = 'Streamer';
    scene.add(this.root);
  }

  update(x: number, z: number, maxNew = 9): void {
    if (!Number.isFinite(x) || !Number.isFinite(z)) {
      return;
    }
    const wanted = new Set(residentKeysAt(x, z));
    for (const [key, chunk] of this.loaded) {
      if (!wanted.has(key)) {
        chunk.unload(this.root, this.physics);
        this.loaded.delete(key);
      }
    }
    let added = 0;
    const budget = Number.isFinite(maxNew) && maxNew > 0 ? maxNew : 1;
    for (const key of wanted) {
      if (this.loaded.has(key)) {
        continue;
      }
      if (added >= budget) {
        break;
      }
      const sep = key.indexOf(',');
      const cx = Number(key.slice(0, sep));
      const cz = Number(key.slice(sep + 1));
      const chunk = new Chunk(cx, cz);
      chunk.load(this.root, this.physics);
      this.loaded.set(key, chunk);
      added += 1;
    }
  }

  residentKeys(): string[] {
    return [...this.loaded.keys()];
  }

  get residentCount(): number {
    return this.loaded.size;
  }

  heightAt(x: number, z: number): number {
    return heightAt(x, z);
  }

  dispose(): void {
    for (const chunk of this.loaded.values()) {
      chunk.unload(this.root, this.physics);
    }
    this.loaded.clear();
    this.root.parent?.remove(this.root);
  }
}
